import { Divider, Grid, LinearProgress, Paper, Stack, ToggleButton, Typography } from "@mui/material";
import { TCandidateFlow } from "Components/Candidate/CandidateModel";
import { useNotification } from "Components/Notification";
import { ConfirmationDialog } from "Components/SharedComponents/ConfirmationDialog/ConfirmationDialog";
import { IsSmScreen, useAppDispatch, useAppSelector } from "helpers/hooks";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShButton, ShGreenBtn, ShToggleButtonGroup } from "shared/SharedStyles/ShInputs";
import { resetAuth } from "store/slices/auth-v2/auth-v2-slice";
import { completeJobApplication, resetCompleteJobApplication, updateSurveyAnswer } from "store/slices/candidate/survey/survey-slice";

export const Survey = ({ navigatedFrom }: { navigatedFrom: TCandidateFlow }) => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const currentPath = useLocation().pathname;
    const notification = useNotification();
    const isSmScreen = IsSmScreen();
    const { surveyQuestions, getSurveyQuestionsStatus, isSurveyCompleted,
        completeJobApplicationStatus, completeJobApplicationResponse
    } = useAppSelector((state) => state.candidate.survey);
    const { candidateDetails } = useAppSelector((state) => state.candidate.candidateProfile);
    const { candidateAssessmentMetadata } = useAppSelector((state) => state.metadata.assessmentMeta);
    const [isSurveyConfirmationOpen, setIsSurveyConfirmationOpen] = useState<boolean>(false);

    const updateAnswer = (qId: number, answerId: number) => {
        dispatch(updateSurveyAnswer({
            payload: { question_id: qId, answer_value: answerId }
        }))
    }

    // To be removed before moving to prod
    const autoFill = () => {
        if (surveyQuestions && candidateAssessmentMetadata) {
            surveyQuestions.forEach((sq, sqIndx) => {
                if (sqIndx % 2 === 0) {
                    updateAnswer(sq.id, candidateAssessmentMetadata.survey?.personality[Math.floor(Math.random() * 4)].id);
                } else {
                    updateAnswer(sq.id, candidateAssessmentMetadata.survey?.interest[Math.floor(Math.random() * 4)].id);
                }
            })
        }
    };

    useEffect(() => {
        if (completeJobApplicationStatus === 'success') {
            notification.displayNotification({
                open: true,
                type: 'success',
                message: completeJobApplicationResponse ?? ''
            });
            navigate(`/candidate/home`);
        } else if (completeJobApplicationStatus === 'failed') {
            notification.displayNotification({
                open: true,
                type: 'error',
                message: completeJobApplicationResponse ?? ''
            });
        }

    }, [completeJobApplicationResponse, completeJobApplicationStatus, dispatch, navigate, notification]);

    const closeConfirmationDialog = () => {
        setIsSurveyConfirmationOpen(false);
        dispatch(resetCompleteJobApplication());
    }

    return (<>
        <Stack rowGap={2}>
            <Typography variant="body2">
                There are no wrong answers and your specific answers aren't shown to anyone.
                You'll receive an in-depth report of your unique traits at the end. It only takes 10 minutes to complete!
            </Typography>
            <Typography variant="caption">
                Tip: The first answer that comes to mind is usually the most accurate one.
            </Typography>
            <Divider />
            <Typography variant="body2">
                Please rate your experience level for each of the following questions.
                Your answers will be included in your application.
            </Typography>
            <Typography variant="caption">
                <strong>{surveyQuestions?.filter(q => q.answer !== null).length}</strong>&nbsp;
                questions answered out of <strong>{surveyQuestions?.length}</strong>.
            </Typography>
            <ShButton variant="outlined" onClick={autoFill}>Auto fill (staging-only!)</ShButton>
            <Stack overflow='auto' rowGap={1} maxHeight={450} minHeight={200} paddingRight={1}>
                {getSurveyQuestionsStatus === 'pending' ?
                    <LinearProgress /> :
                    surveyQuestions?.map((question) => (
                        <Paper variant="outlined" key={question.id}>
                            <Grid container padding={1} spacing={1} alignItems='center'>
                                <Grid item xs={12} sm={12} md={12} lg={5} xl={5}>
                                    <Typography variant="body2">
                                        {question.text}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={12} md={12} lg={7} xl={7} textAlign='end'>
                                    <ShToggleButtonGroup color="primary" size="small" exclusive fullWidth={isSmScreen}
                                        orientation={isSmScreen ? 'vertical' : 'horizontal'}
                                        disabled={question?.updateSurveyAnswerStatus === 'pending'}
                                        value={question.answer}>
                                        {candidateAssessmentMetadata?.survey[question.kind].map((option) => (
                                            <ToggleButton value={option.id} key={option.id}
                                                onClick={() => option.id !== question.answer && updateAnswer(question.id, option.id)}>
                                                {option.name}
                                            </ToggleButton>
                                        ))}
                                    </ShToggleButtonGroup>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
            </Stack>
        </Stack>
        <Stack direction='row' justifyContent='space-between' marginTop={1}>
            <ShButton variant="outlined" onClick={() => {
                if (navigatedFrom === 'apply_job') {
                    navigate(`${currentPath.replace('assessment', 'questionnaire')}`)
                } else {
                    dispatch(resetAuth());
                    navigate('/candidate/login');
                }
            }}>Back</ShButton>
            <ShGreenBtn variant="outlined" size="small" disabled={!isSurveyCompleted} onClick={() => {
                if (navigatedFrom === 'apply_job') {
                    setIsSurveyConfirmationOpen(true)
                } else {
                    navigate('/candidate/home');
                }
            }}>Next</ShGreenBtn>
        </Stack>
        <ConfirmationDialog isDialogOpen={isSurveyConfirmationOpen} onCancel={() => closeConfirmationDialog()}
            onConfirm={() => candidateDetails?.jobApplicationId &&
                dispatch(completeJobApplication(candidateDetails?.jobApplicationId))}
            cancelButtonLabel="Review Answers" title="Well done!"
            contentText="Complete your job application by hitting submit or review your answers below."
            isCancelDisabled={completeJobApplicationStatus === "pending"}
            isConfirmDisabled={completeJobApplicationStatus === "pending"} />
    </>)
}